import React, { Component } from 'react';
import { injectIntl } from 'react-intl';
import { Switch, Route } from 'react-router';
import injectTapEventPlugin from 'react-tap-event-plugin';
import Helmet from './helmet';

import styles from './index.module.scss';
import Layout from './layout';
// import CookieConsent from 'react-cookie-consent';

import routeTemplates from 'ui/common/routes/templates';
import Authenticated from 'ui/common/auth/authenticated';

import AuthenticatePromptModal from 'ui/auth/prompt-modal';
import ConfirmationDialog from 'ui/common/confirmation-dialog';
import NewEducationEntryDialog from 'ui/education/new';
import NewWorkExperienceEntryDialog from 'ui/work-experience/new';
import Company from 'ui/pages/company';
import AboutUs from 'ui/pages/about-us';
import ContactUs from 'ui/pages/contact-us';
import Faq from 'ui/pages/faq/loadable';
import TermsOfUse from 'ui/auth/consent';
import PrivacyPolicy from 'ui/pages/privacy-policy';
import CookiePolicy from 'ui/pages/cookie-consent';
import SignUp from 'ui/auth/sign-up/loadable';
import VerificationEmailSent from 'ui/auth/verification-email-sent/loadable';
import ResendVerificationEmail from 'ui/auth/resend-verification-email/loadable';
import VerifyEmail from 'ui/auth/verify-email/loadable';
import Login from 'ui/auth/login/loadable';
import ChangePassword from 'ui/auth/change-password/loadable';
import LinkedAccounts from 'ui/auth/linked-accounts/loadable';
import SetPassword from 'ui/auth/set-password/loadable';
import ForgotPassword from 'ui/auth/forgot-password/loadable';
import ResetPassword from 'ui/auth/reset-password/loadable';
import ApprovalRequired from 'ui/auth/approval-required';
import UserProfile from 'ui/users/details/loadable';
import CompanyProfile from 'ui/companies/details/loadable';
import EditCompany from 'ui/companies/edit/loadable';
import ThankYou from 'ui/pages/thank-you/loadable';
import BlockChain101 from 'ui/blockchain-101/loadable';
import CompanyLandingPage from 'ui/pages/company-landing-page/loadable';
import Validate from 'ui/pages/validate/loadable';
import SignUpWizard from 'ui/auth/sign-up-wizard/loadable';
import AcceptAsAdvisor from 'ui/companies/accept-as-advisor/loadable';
import RevokeAsAdvisor from 'ui/companies/revoke-as-advisor/loadable';
import AcceptAsTeamMember from 'ui/companies/accept-as-team-member/loadable';
import RevokeAsTeamMember from 'ui/companies/revoke-as-team-member/loadable';
import IcoListing from 'ui/companies/listing/loadable';
import AcceptAdvisorConnection from 'ui/users/advisories/loadable';
import GithubAuthCallback from 'ui/pages/github-auth-callback';
import LinkedInCsv from 'ui/graphql/LinkedInCsv/loadable';
import ListCompanies from 'ui/graphql/ListCompanies/loadable';
import MyEvaluations from 'ui/graphql/MyEvaluations/loadable';
import JobRoles from 'ui/graphql/JobRoles/loadable';
import SelectSkills from 'ui/graphql/SelectSkills/loadable';
import AssignmentDetails from 'ui/graphql/AssignmentDetails/loadable';
import ValidatorsLeaderboard from 'ui/graphql/ValidatorsLeaderboard/loadable';
import AssessmentMagicLink from 'ui/graphql/AssessmentMagicLink/loadable';
import AdminValidatorDetails from 'ui/graphql/AdminValidatorDetails/loadable';
import ViewCandidates from 'ui/graphql/ViewCandidates/loadable';
import HiringDashboard from 'ui/graphql/HiringDashboard/loadable';
import PartnerAcceptInvite from 'ui/graphql/PartnerAcceptInvite/loadable';
import AdminUpdateValidator from 'ui/graphql/AdminUpdateValidator/loadable';
import ClaimReportDetails from 'ui/claim-reports/details';
import BecomeAValidator from 'ui/validator/become-a-validator/loadable';
import DevControlPanelAuditTable from 'ui/graphql/DevControlPanelAuditTable/loadable';
import UserAcceptProgramInvite from 'ui/graphql/UserAcceptProgramInvite/loadable';
import BehaviouralAssessmentsQuiz from 'ui/graphql/BehaviouralAssessmentsQuiz/loadable';
import CandidateAcceptProfilerInvite from 'ui/graphql/CandidateAcceptProfilerInvite/loadable';
import AdminProfilerCandidates from 'ui/graphql/AdminProfilerCandidates/loadable';
import ChatbotsForProfilerCandidate from 'ui/graphql/ChatbotsForProfilerCandidate/loadable';
import ChatbotReport from 'ui/graphql/ChatbotReport/loadable';
import CodeReviewProof from 'ui/graphql/AdminProfilerCandidates/codeReviewProofPage';

import Admin from 'ui/admin/loadable';
import Users from 'ui/users/loadable';
import Claims from 'ui/claims/loadable';
import Votes from 'ui/votes/loadable';
import Badges from 'ui/badges/loadable';
import Assignment from 'ui/assignment/loadable';
import Thanks from 'ui/pages/assignment-thanks-page/loadable';
import AdminAcceptThanks from 'ui/pages/company-admin-accept-thanks/loadable';
import ValidatorFeatures from 'ui/validator/loadable';
import DevControlPanel from 'ui/graphql/DevControlPanel/loadable';
import ChatbotView from 'ui/pages/chatbot-view/loadable';
import ChatbotQuiz from 'ui/graphql/ChatbotQuiz/loadable';
import ChatbotQuizSubmissionPart2 from 'ui/graphql/ChatbotQuizSubmissionPart2/loadable';
// import ChatbotQuiz from 'ui/graphql/ChatbotQuiz/index';

/*
  DO NOT delete import-new-route-top
  Code generator needs those two to dynamically import route for you.
*/

// import-new-route-top

// Needed for onTouchTap
// http://stackoverflow.com/a/34015469/988941
injectTapEventPlugin();

export const AuthenticatedUserProfile = Authenticated(UserProfile);

class App extends Component {
  render() {
    return (
      <div className={styles.app}>
        <Helmet />
        <Switch>
          <Route
            exact
            path={routeTemplates.auth.verificationEmailSent}
            component={VerificationEmailSent}
          />
          <Route
            exact
            path={routeTemplates.auth.resendVerificationEmail}
            component={ResendVerificationEmail}
          />
          <Route exact path={routeTemplates.auth.verifyEmail} component={VerifyEmail} />
          <Route exact path={routeTemplates.auth.forgotPassword} component={ForgotPassword} />
          <Route exact path={routeTemplates.auth.resetPassword} component={ResetPassword} />
          <Route exact path={routeTemplates.auth.approvalRequired} component={ApprovalRequired} />
          <Route path={routeTemplates.thankYou} component={ThankYou} />

          <Route exact path={routeTemplates.githubAuth} component={GithubAuthCallback} />

          <Route exact path={routeTemplates.auth.login} component={Login} />
          <Route exact path={routeTemplates.auth.signUp} component={SignUp} />
          <Route
            exact
            path={routeTemplates.program.behaviouralAssessment}
            component={BehaviouralAssessmentsQuiz}
          />
          <Route path={routeTemplates.chatbotReports.details} exact component={ChatbotReport} />
          <Layout>
            <Route exact path={routeTemplates.root} component={AuthenticatedUserProfile} />
            <Route exact path={routeTemplates.aboutCompany} component={Company} />
            <Route exact path={routeTemplates.termsOfUse} component={TermsOfUse} />
            <Route exact path={routeTemplates.privacyPolicy} component={PrivacyPolicy} />
            <Route exact path={routeTemplates.cookieConsent} component={CookiePolicy} />
            <Route exact path={routeTemplates.faq} component={Faq} />
            <Route exact path={routeTemplates.aboutUs} component={AboutUs} />
            <Route exact path={routeTemplates.contactUs} component={ContactUs} />
            <Route exact path={routeTemplates.company} component={CompanyLandingPage} />
            {/* <Route exact path={routeTemplates.merkle} component={Merkle} /> */}
            <Route
              exact
              path={routeTemplates.users.myProfile}
              component={AuthenticatedUserProfile}
              name="myProfile"
            />
            <Route exact path={routeTemplates.imports.linkedInCsv} component={LinkedInCsv} />
            <Route exact path={routeTemplates.auth.changePassword} component={ChangePassword} />
            <Route exact path={routeTemplates.auth.setPassword} component={SetPassword} />
            <Route exact path={routeTemplates.auth.linkedAccounts} component={LinkedAccounts} />
            <Route exact path={routeTemplates.wizard.signUp} component={SignUpWizard} />
            <Route path={routeTemplates.admin.root} component={Admin} />
            <Route path={routeTemplates.admin.companies.accept} component={AdminAcceptThanks} />
            <Route path={routeTemplates.users.root} component={Users} />
            <Route path={routeTemplates.claims.root} component={Claims} />
            <Route path={routeTemplates.claimReports.details} component={ClaimReportDetails} />

            <Route path={routeTemplates.votes.root} component={Votes} />
            <Route path={routeTemplates.validatorFeatures.root} component={ValidatorFeatures} />
            <Route path={routeTemplates.chatbotQuiz.quiz} exact component={ChatbotQuiz} />
            <Route
              path={routeTemplates.chatbotQuiz.quizSubmission}
              exact
              component={ChatbotQuizSubmissionPart2}
            />

            <Route
              exact
              path={routeTemplates.companies.acceptAdvisorConnection}
              component={AcceptAdvisorConnection}
            />
            <Route
              exact
              path={routeTemplates.companies.acceptAsAdvisor}
              component={AcceptAsAdvisor}
            />
            <Route
              exact
              path={routeTemplates.companies.revokeAsAdvisor}
              component={RevokeAsAdvisor}
            />
            <Route
              exact
              path={routeTemplates.companies.acceptAsTeamMember}
              component={AcceptAsTeamMember}
            />
            <Route
              exact
              path={routeTemplates.companies.revokeAsTeamMember}
              component={RevokeAsTeamMember}
            />
            <Route exact path={routeTemplates.companies.root} component={ListCompanies} />
            <Route exact path={routeTemplates.companies.listing.root} component={IcoListing} />

            <Route exact path={routeTemplates.companies.vanityProfile} component={CompanyProfile} />
            <Route exact path={routeTemplates.companies.edit} component={EditCompany} />
            <Route
              exact
              path={routeTemplates.profiler.details}
              component={AdminProfilerCandidates}
            />
            <Route
              exact
              path={routeTemplates.profiler.inviteCandidateToChatbots}
              component={ChatbotsForProfilerCandidate}
            />
            <Route
              exact
              path={routeTemplates.companies.magicLinkCandidates}
              component={ViewCandidates}
            />

            <Route
              exact
              path={routeTemplates.companies.hiringDashboard}
              component={HiringDashboard}
            />
            <Route exact path={routeTemplates.blockchain101.root} component={BlockChain101} />

            <Route exact path={routeTemplates.validate} component={Validate} />

            <Route exact path={routeTemplates.roles.root} component={JobRoles} />
            <Route exact path={routeTemplates.roles.skills} component={SelectSkills} />

            <Route exact path={routeTemplates.assignments.root} component={Assignment} />
            <Route exact path={routeTemplates.assignments.thanks} component={Thanks} />
            <Route
              exact
              path={routeTemplates.assignments.myEvaluations}
              component={MyEvaluations}
            />
            <Route exact path={routeTemplates.assignments.details} component={AssignmentDetails} />
            <Route
              exact
              path={routeTemplates.validatorsLeaderboard}
              component={ValidatorsLeaderboard}
            />

            <Route
              path={routeTemplates.admin.validators.details}
              component={AdminValidatorDetails}
            />
            <Route path={routeTemplates.admin.validators.update} component={AdminUpdateValidator} />
            <Route path={routeTemplates.codeReviewProof} component={CodeReviewProof} />
            <Route
              exact
              path={routeTemplates.magicLinks.assessmentMagicLink}
              component={AssessmentMagicLink}
            />
            <Route path={routeTemplates.program.user} component={UserAcceptProgramInvite} />
            <Route path={routeTemplates.program.platformUser} component={UserAcceptProgramInvite} />
            <Route
              exact
              path={routeTemplates.partner.acceptInvite}
              component={PartnerAcceptInvite}
            />

            <Route
              exact
              path={routeTemplates.profiler.candidateAcceptInvite}
              component={CandidateAcceptProfilerInvite}
            />

            <Route exact path={routeTemplates.becomeAValidator} component={BecomeAValidator} />
            <Route exact path={routeTemplates.devControlPanel.root} component={DevControlPanel} />
            <Route
              exact
              path={routeTemplates.devControlPanel.auditTrail}
              component={DevControlPanelAuditTable}
            />
            <Route exact path={routeTemplates.chatbotView} component={ChatbotView} />

            {/* DO NOT remove add-new-route-top */}
            {/* Code generator needs those to insert route dynamically */}
            {/* add-new-route-top */}
            <Route path={routeTemplates.badges.root} component={Badges} />

            {/* This should always be last */}
            <Route exact path={routeTemplates.users.vanityProfile} component={UserProfile} />
          </Layout>
        </Switch>
        <AuthenticatePromptModal />
        <ConfirmationDialog />
        <NewEducationEntryDialog />
        <NewWorkExperienceEntryDialog />
        {/* <CookieConsent
          buttonText="OK, Understood"
          cookieName="indorse-cookie-v1"
          expires={365}
          acceptOnScroll={true}
          acceptOnScrollPercentage={25}
          cookieValue="accepted"
        >
          This website uses cookies to enhance the user experience. By clicking 'OK' or by
          continuing to use Indorse, you agree to the policy. For more information please review our
          cookie policy. <Link to="/cookie-policy">Cookie Policy</Link>{' '}
        </CookieConsent> */}
      </div>
    );
  }

  unstable_handleError(e) {
    console.log(e.message);
  }
}

export default injectIntl(App);

// I wrote this comment to trigger deploy



// WEBPACK FOOTER //
// ./src/ui/app/index.js